import React from 'react';
import PropTypes from 'prop-types';
import camelcase from 'lodash/camelCase';
import get from 'lodash/get';
import noop from 'lodash/noop';
import isEmpty from 'lodash/isEmpty';
import { trackingLocations, trackClickTypes, trackingApplication } from './consumer';

export const HostContext = React.createContext();

export default class HostContextProvider extends React.Component {
  static propTypes = {
    trackClick: PropTypes.func,
    trackView: PropTypes.func,
    clickTrackingScholarships: PropTypes.func,
    uuid: PropTypes.string,
    scid: PropTypes.number,
  };

  constructor(props) {
    super(props);

    this.lastView = null;
    this.hubType = null;
    this.gaBacklog = [];
    this.premium = false;
    this.gaRoot = null;
    this.pageViewSent = false;
    this.gaInitialViewSent = false;
    this.lastLocation = null;
  }

  render() {
    const trackClick = get(this, 'props.trackClick', noop);
    const trackView = get(this, 'props.trackView', noop);
    const clickTrackingScholarships = get(this, 'props.clickTrackingScholarships', noop);
    const getUrls = get(this, 'props.getUrls', noop);
    const isCamino = get(this, 'props.isCamino', false);
    const isHE = get(this, 'props.isHE', false);
    const connectToCollege = get(this, 'props.connectToCollege');
    const headerMessage = get(this, 'props.headerMessage');
    const topAreasOfStudyTitle = get(this, 'props.topAreasOfStudyTitle');
    const addToTransferPlan = get(this, 'props.addToTransferPlan', noop);
    const { uuid, scid } = this.props;
    return (
      <HostContext.Provider
        value={{
          getUrls,
          isCamino,
          isHE,
          connectToCollege,
          headerMessage,
          addToTransferPlan,
          topAreasOfStudyTitle,
          trackEventGa: (payload) => {
            const { type, category, label } = payload;
            if (window.ga && this.gaRoot && this.gaInitialViewSent) {
              window.ga(`${this.gaRoot}.send`, 'event', type, category, label);
            } else {
              this.gaBacklog.push({ gaType: 'event', category, type, label });
            }
          },
          trackViewGa: (payload) => {
            if (!window.ga) {
              return;
            }
            this.premium = payload.premium === true ? true : this.premium;
            const location = `/colleges/hubs/${uuid}/${camelcase(payload.location)}`;
            /* istanbul ignore else*/
            if (!this.gaRoot) {
              window.ga(() => {
                this.gaRoot = get(
                  window.ga.getAll()[0],
                  'a.data.values[":name"]',
                  get(window.ga.getAll()[0], 'b.data.values[":name"]'),
                );
                /* istanbul ignore else */
                if (this.gaRoot) {
                  const premium = this.premium ? 'Premium' : 'Standard';
                  window.ga(`${this.gaRoot}.set`, 'dimension21', premium);
                  window.ga(`${this.gaRoot}.set`, 'dimension22', `${uuid}`);
                  window.ga(`${this.gaRoot}.set`, 'dimension23', `${scid}`);
                  window.ga(`${this.gaRoot}.send`, 'pageview', location);
                  this.lastLocation = location;
                  this.gaInitialViewSent = true;
                  this.gaBacklog.forEach((backlogPayload) => {
                    const { gaType, category, type, label, location } = backlogPayload;
                    /* istanbul ignore else*/
                    if (gaType === 'pageview') {
                      window.ga(`${this.gaRoot}.send`, 'pageview', location);
                    } else if (gaType === 'event') {
                      window.ga('send', 'event', category, type, label);
                    }
                  });
                  this.gaBacklog = [];
                }
              });
            } else if (location !== this.lastLocation) {
              this.lastLocation = location;
              if (this.gaRoot && this.gaInitialViewSent) {
                window.ga(`${this.gaRoot}.send`, 'pageview', location);
              } else {
                this.gaBacklog.push({ gaType: 'pageview', location });
              }
            }
          },
          trackClick: (payload) => {
            /* istanbul ignore else*/
            if (trackClick) {
              const trackingPayload = { ...payload };
              /* istanbul ignore else*/
              if (!trackingPayload.type) {
                trackingPayload.type = trackClickTypes.CLICK;
              }
              /* istanbul ignore else*/
              if (trackingPayload.productId) {
                trackingPayload.productId = Number(payload.productId);
              }
              /* istanbul ignore else*/
              if (payload.location === trackingLocations.TABS.CURRENT_TAB) {
                trackingPayload.location = this.lastView;
              }
              trackClick(trackingPayload);
            }
          },
          trackView: (payload) => {
            /* istanbul ignore else*/
            if (trackView) {
              const trackingPayload = { ...payload };
              /* istanbul ignore else*/
              if (!trackingPayload.type) {
                trackingPayload.type = trackClickTypes.HUBS_VIEW;
              }
              /* istanbul ignore else*/
              if (payload.location === trackingLocations.TABS.CURRENT_TAB) {
                trackingPayload.location = this.lastView;
              }
              // track hubpage once per change of collegeId
              /* istanbul ignore else*/
              if (!this.pageViewSent) {
                this.pageViewSent = true;
                const pageViewPayload = { ...trackingPayload };
                pageViewPayload.location = 'hubpage';
                trackView(pageViewPayload);
                if (!isEmpty(document.referrer)) {
                  trackView({
                    type: trackClickTypes.HUBS_VIEW,
                    location: `${trackingLocations.COLLEGE_PROFILE_REFERRER}_${document.referrer}`,
                  });
                }
              }
              // track all view events on tab changes
              /* istanbul ignore else*/
              if (trackingPayload.location !== this.lastView) {
                this.lastView = trackingPayload.location;
                trackView(trackingPayload);
              }
            }
          },
          clickTrackingScholarships: (payload) => {
            clickTrackingScholarships({
              ...payload,
              application: trackingApplication.COLLEGE_PROFILE,
              type: trackClickTypes.SCHOLARSHIPS,
            });
          },
        }}
      >
        {this.props.children}
      </HostContext.Provider>
    );
  }
}
